/**
 * ネットワーク接続種別の監視フック
 *
 * Network Information API (navigator.connection) で WiFi / セルラーを判定する。
 * 非対応ブラウザ（Safari 等）では判定不能として WiFi 扱いにフォールバックする。
 */
import { useEffect, useState } from 'react'

type NetworkInformation = EventTarget & {
  type?: string
  effectiveType?: string
  saveData?: boolean
  downlink?: number
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation
  mozConnection?: NetworkInformation
  webkitConnection?: NetworkInformation
}

export type NetworkStatus = {
  /** WiFi・有線など従量課金でない回線とみなせるか */
  isWifi: boolean
  /** セルラー回線と判定できたか */
  isCellular: boolean
  /** データセーバーが有効か */
  saveData: boolean
  /** '4g' | '3g' | '2g' | 'slow-2g' など */
  effectiveType: string | null
  /** Network Information API が利用可能か */
  isSupported: boolean
}

const getConnection = (): NetworkInformation | undefined => {
  const nav = navigator as NavigatorWithConnection
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection
}

const CELLULAR_TYPES = new Set(['cellular', 'wimax'])

const readStatus = (): NetworkStatus => {
  const connection = getConnection()
  if (!connection) {
    return { effectiveType: null, isCellular: false, isSupported: false, isWifi: true, saveData: false }
  }

  const type = connection.type ?? null
  const isCellular = type !== null && CELLULAR_TYPES.has(type)
  // type が取れないブラウザでも saveData が有効なら従量課金回線とみなす
  const saveData = connection.saveData === true
  return {
    effectiveType: connection.effectiveType ?? null,
    isCellular,
    isSupported: true,
    isWifi: !isCellular && !saveData,
    saveData,
  }
}

export const useNetworkStatus = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>(readStatus)

  useEffect(() => {
    const connection = getConnection()
    if (!connection) return

    const handleChange = () => setStatus(readStatus())
    connection.addEventListener('change', handleChange)
    return () => connection.removeEventListener('change', handleChange)
  }, [])

  return status
}
